import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, Plus, Minus, Clock, FileText,
  Calculator, RefreshCw, Printer, CheckCircle, AlertCircle
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

const POSCashDrawerPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [drawerStatus, setDrawerStatus] = useState({
    is_open: false,
    opened_at: null,
    opening_balance: 0,
    current_balance: 0,
    cash_sales: 0,
    cash_refunds: 0,
    cash_in: 0,
    cash_out: 0
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCash, setShowAddCash] = useState(false);
  const [showRemoveCash, setShowRemoveCash] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [cashReason, setCashReason] = useState('');
  const [openingBalance, setOpeningBalance] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchDrawerStatus();
      fetchTransactions();
    }
  }, [bid]);

  const fetchDrawerStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pos/cash-drawer/status');
      setDrawerStatus(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load drawer status'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const { data } = await api.get('/pos/cash-drawer/transactions');
      setTransactions(data.transactions || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load transactions'));
    }
  };

  const handleOpenDrawer = async () => {
    if (!openingBalance) {
      toast.error('Enter opening balance');
      return;
    }
    try {
      await api.post('/pos/cash-drawer/open', { opening_balance: parseFloat(openingBalance) });
      toast.success('Cash drawer opened');
      setOpeningBalance('');
      fetchDrawerStatus();
      fetchTransactions();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error opening drawer'));
    }
  };

  const handleCloseDrawer = () => {
    setConfirmModal({
      title: 'Close Cash Drawer',
      description: 'Close the cash drawer? Make sure to count the cash first.',
      confirmLabel: 'Close Drawer',
      confirmClass: 'bg-slate-800 hover:bg-slate-700',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.post('/pos/cash-drawer/close');
          toast.success('Cash drawer closed');
          fetchDrawerStatus();
          fetchTransactions();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error closing drawer'));
        }
      }
    });
  };

  const handleCashMovement = async (type) => {
    if (!cashAmount || parseFloat(cashAmount) <= 0) {
      toast.error('Enter valid amount');
      return;
    }
    if (!cashReason) {
      toast.error('Enter reason');
      return;
    }
    try {
      await api.post(`/pos/cash-drawer/${type}`, {
        amount: parseFloat(cashAmount),
        reason: cashReason
      });
      toast.success(`Cash ${type === 'add' ? 'added' : 'removed'} successfully`);
      setCashAmount('');
      setCashReason('');
      setShowAddCash(false);
      setShowRemoveCash(false);
      fetchDrawerStatus();
      fetchTransactions();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error processing cash movement'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/business/${bid}/pos`)} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Cash Drawer</h1>
            <p className="text-sm text-slate-500">Manage cash drawer operations</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {drawerStatus.is_open ? (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
              <CheckCircle className="w-4 h-4" /> Drawer Open
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">
              <AlertCircle className="w-4 h-4" /> Drawer Closed
            </span>
          )}
        </div>
      </div>

      {/* Drawer Status Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {drawerStatus.is_open ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <p className="text-xs text-slate-500 mb-1">Opening Balance</p>
                <p className="text-2xl font-bold text-slate-800">₹{(drawerStatus.opening_balance || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Cash Sales</p>
                <p className="text-2xl font-bold text-emerald-600">+₹{(drawerStatus.cash_sales || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Cash In/Out</p>
                <p className="text-2xl font-bold text-blue-600">
                  {(drawerStatus.cash_in - drawerStatus.cash_out) >= 0 ? '+' : ''}
                  ₹{((drawerStatus.cash_in || 0) - (drawerStatus.cash_out || 0)).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Expected Balance</p>
                <p className="text-2xl font-bold text-slate-800">₹{(drawerStatus.current_balance || 0).toLocaleString()}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => { setCashAmount(''); setCashReason(''); setShowAddCash(true); }}
                className="flex items-center gap-2 px-4 py-2.5 bg-emerald-100 text-emerald-700 font-medium rounded-lg hover:bg-emerald-200"
              >
                <Plus className="w-4 h-4" /> Add Cash
              </button>
              <button
                onClick={() => { setCashAmount(''); setCashReason(''); setShowRemoveCash(true); }}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200"
              >
                <Minus className="w-4 h-4" /> Remove Cash
              </button>
              <button
                onClick={handleCloseDrawer}
                className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-700"
              >
                <Clock className="w-4 h-4" /> Close Drawer
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <Calculator className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Cash Drawer is Closed</h3>
            <p className="text-slate-500 mb-6">Open the drawer to start accepting cash payments</p>
            <div className="max-w-xs mx-auto space-y-3">
              <input
                type="number"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(e.target.value)}
                placeholder="Enter opening balance"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-center text-lg"
              />
              <button
                onClick={handleOpenDrawer}
                className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
              >
                Open Cash Drawer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800">Today's Cash Movements</h2>
        </div>
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No transactions today</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {transactions.map((txn, i) => (
              <div key={i} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800">{txn.transaction_type || txn.type}</p>
                  <p className="text-sm text-slate-500">{txn.description || txn.reason || txn.reference}</p>
                  <p className="text-xs text-slate-400">{txn.created_at ? new Date(txn.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}</p>
                </div>
                <p className={`font-semibold ${txn.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {txn.amount >= 0 ? '+' : ''}₹{Math.abs(txn.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Cash Modal */}
      {showAddCash && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Add Cash to Drawer</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Amount</label>
                <input
                  type="number"
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Reason</label>
                <input
                  type="text"
                  value={cashReason}
                  onChange={(e) => setCashReason(e.target.value)}
                  placeholder="e.g., Change float, Petty cash"
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAddCash(false)} className="flex-1 py-2.5 border border-slate-200 rounded-lg font-medium text-slate-600">
                Cancel
              </button>
              <button onClick={() => handleCashMovement('add')} className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700">
                Add Cash
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Cash Modal */}
      {showRemoveCash && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Remove Cash from Drawer</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Amount</label>
                <input
                  type="number"
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Reason</label>
                <input
                  type="text"
                  value={cashReason}
                  onChange={(e) => setCashReason(e.target.value)}
                  placeholder="e.g., Bank deposit, Expense"
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowRemoveCash(false)} className="flex-1 py-2.5 border border-slate-200 rounded-lg font-medium text-slate-600">
                Cancel
              </button>
              <button onClick={() => handleCashMovement('remove')} className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700">
                Remove Cash
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default POSCashDrawerPage;
